/**
 * Search, product-filter, and detail-modal behavior for the Dose Ingredients
 * grid (migrated from dose-daily's sections/ingredients.liquid).
 *
 * Cards are plain buttons; each has a sibling <template> holding its modal
 * content, cloned into the shared native <dialog> on open. `showModal()`
 * gives us focus trapping, an inert background, and Escape-to-close for
 * free — no hand-rolled tab-trap logic needed.
 *
 * The active product filter is single-select ("View All" by default) and is
 * mirrored to the URL as `?product=<handle>` so the filtered view is
 * linkable and restores on load/back-forward navigation.
 */

class DoseIngredients extends HTMLElement {
  connectedCallback() {
    this.searchInput = this.querySelector('.dose-ingredients__search-input');
    this.filterGroup = this.querySelector('[data-filters]');
    this.filters = [...this.querySelectorAll('.dose-ingredients__filter')];
    this.grid = this.querySelector('[data-grid]');
    this.items = [...this.querySelectorAll('[data-item]')];
    this.productPanels = [...this.querySelectorAll('[data-product-panel]')];
    this.emptyMessage = this.querySelector('[data-empty]');
    this.announcer = this.querySelector('[data-announcer]');
    this.dialog = this.querySelector('[data-dialog]');
    this.dialogBody = this.querySelector('[data-dialog-body]');
    this.closeButton = this.querySelector('[data-close]');

    if (!this.items.length || !this.filters.length) return;

    this.templates = new Map();
    for (const template of this.querySelectorAll('template[data-ingredient-content]')) {
      this.templates.set(template.dataset.ingredientContent, template);
    }

    this.activeProduct = 'all';
    this.activeFilterButton = null;
    this.lastFocusedCard = null;

    this.filterGroup.addEventListener('click', this.#handleFilterClick);
    this.filterGroup.addEventListener('keydown', this.#handleFilterKeydown);
    this.searchInput?.addEventListener('input', this.#handleSearchInput);
    this.grid.addEventListener('click', this.#handleGridClick);
    this.closeButton?.addEventListener('click', this.#handleCloseClick);
    this.dialog?.addEventListener('click', this.#handleDialogClick);
    this.dialog?.addEventListener('close', this.#handleDialogClose);
    window.addEventListener('popstate', this.#handlePopState);
    document.addEventListener('shopify:block:select', this.#handleBlockSelect);

    this.syncFilterFromUrl({ updateHistory: false, announce: false });
  }

  disconnectedCallback() {
    window.removeEventListener('popstate', this.#handlePopState);
    document.removeEventListener('shopify:block:select', this.#handleBlockSelect);
  }

  /** Selecting a Product block in the theme editor previews it like clicking its filter would. */
  #handleBlockSelect = (event) => {
    const filter = event.target.closest?.('.dose-ingredients__filter');
    if (!filter || !this.filters.includes(filter)) return;
    this.setActiveFilter(filter);
  };

  /**
   * Reads `?product=<handle>` from the current URL and activates the
   * matching filter button, falling back to "View All" when absent or
   * unrecognized.
   */
  syncFilterFromUrl(options) {
    const requested = new URLSearchParams(window.location.search).get('product');
    const match = requested && this.filters.find((filter) => filter.dataset.product === requested);
    this.setActiveFilter(match || this.filters[0], options);
  }

  #handlePopState = () => {
    this.syncFilterFromUrl({ updateHistory: false, announce: true });
  };

  #handleFilterClick = (event) => {
    const filter = event.target.closest('.dose-ingredients__filter');
    if (!filter || !this.filterGroup.contains(filter)) return;
    this.setActiveFilter(filter);
  };

  /**
   * Roving-tabindex arrow-key navigation for the single-select filter
   * radiogroup (Left/Right/Up/Down/Home/End), matching the WAI-ARIA radio
   * group pattern.
   */
  #handleFilterKeydown = (event) => {
    const filter = event.target.closest('.dose-ingredients__filter');
    if (!filter) return;

    const index = this.filters.indexOf(filter);
    let next = null;

    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
      next = this.filters[(index + 1) % this.filters.length];
    } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
      next = this.filters[(index - 1 + this.filters.length) % this.filters.length];
    } else if (event.key === 'Home') {
      next = this.filters[0];
    } else if (event.key === 'End') {
      next = this.filters[this.filters.length - 1];
    }

    if (!next) return;

    event.preventDefault();
    next.focus();
    this.setActiveFilter(next);
  };

  /**
   * Activates a single filter button, updates the URL, and re-applies
   * search/filter matching. There can only be one active product filter at
   * a time.
   *
   * @param {HTMLElement} filter
   * @param {{updateHistory?: boolean, announce?: boolean}} [options]
   */
  setActiveFilter(filter, { updateHistory = true, announce = true } = {}) {
    if (!filter || filter === this.activeFilterButton) return;

    this.activeFilterButton = filter;
    this.activeProduct = filter.dataset.product;

    for (const button of this.filters) {
      const selected = button === filter;
      button.setAttribute('aria-checked', selected ? 'true' : 'false');
      button.setAttribute('tabindex', selected ? '0' : '-1');
    }

    if (updateHistory) {
      const url = new URL(window.location.href);
      if (this.activeProduct === 'all') {
        url.searchParams.delete('product');
      } else {
        url.searchParams.set('product', this.activeProduct);
      }
      window.history.pushState({}, '', url);
    }

    this.updateProduct();
    this.applyFilters({ announce });
  }

  /**
   * Shows the product panel matching the active product filter and hides
   * the rest. "View All" has no matching panel, so every panel stays hidden
   * without needing a special case.
   */
  updateProduct() {
    for (const panel of this.productPanels) {
      panel.hidden = panel.dataset.productPanel !== this.activeProduct;
    }
  }

  #handleSearchInput = () => {
    this.applyFilters({ announce: true });
  };

  /**
   * Re-evaluates search + active product filter against every card, toggles
   * each item's `hidden` attribute, and reports the visible count in the
   * assertive live region.
   *
   * @param {{announce?: boolean}} [options]
   */
  applyFilters({ announce = true } = {}) {
    const query = this.searchInput?.value.trim().toLowerCase() || '';
    let visibleCount = 0;

    for (const item of this.items) {
      const card = item.querySelector('.dose-ingredients__card');
      const name = card?.dataset.name || '';
      const products = (card?.dataset.products || '').split(',').filter(Boolean);

      const matchesSearch = query === '' || name.includes(query);
      const matchesProduct = this.activeProduct === 'all' || products.includes(this.activeProduct);
      const visible = matchesSearch && matchesProduct;

      item.hidden = !visible;
      if (visible) visibleCount += 1;
    }

    if (this.emptyMessage) this.emptyMessage.hidden = visibleCount !== 0;

    if (announce && this.announcer) {
      this.announcer.textContent =
        visibleCount === 0
          ? 'No ingredients matched your search.'
          : `${visibleCount} ingredient${visibleCount === 1 ? '' : 's'} shown.`;
    }
  }

  #handleGridClick = (event) => {
    const card = event.target.closest('.dose-ingredients__card');
    if (!card || !this.grid.contains(card)) return;
    this.openModal(card);
  };

  openModal(card) {
    const template = this.templates.get(card.dataset.ingredient);
    if (!template) return;

    this.dialogBody.replaceChildren(template.content.cloneNode(true));
    this.lastFocusedCard = card;
    this.dialog.showModal();
    this.closeButton?.focus();
  }

  #handleCloseClick = () => {
    this.dialog.close();
  };

  /** Clicking the ::backdrop (or the dialog's own padding) closes it — a click on any real content lands on a descendant, never on `this.dialog` itself. */
  #handleDialogClick = (event) => {
    if (event.target === this.dialog) this.dialog.close();
  };

  #handleDialogClose = () => {
    this.lastFocusedCard?.focus();
  };
}

if (!customElements.get('dose-ingredients')) {
  customElements.define('dose-ingredients', DoseIngredients);
}
